import {
  Box, Button, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setFilter, resetFilters } from 'app/slice/executive.slice'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { ExecutiveFilterState, ExecutiveTab } from 'api/executive/executive.types'
import { useDoctors, useOperativeTypes, useProcedures, usePromotions } from 'features/executive/shared/hooks/use-reference-data'
import { useEffect, useMemo, useState } from 'react'
import { colors } from 'constants/theme'
import ExcelCodeFilterSelect, { CodeRow } from 'component/Search/ExcelCodeFilterSelect'
import { EXECUTIVE_TAB_FILTER_KEYS, pruneIdSelection } from './filter-config'

interface Props {
  tab: ExecutiveTab
  open: boolean
  onClose: () => void
}

// Per-tab advanced-filter set lives in ./filter-config (shared with ExecutiveFilterBar).

type FilterOption = { value: string | number; label: string }
type OptionMap = Partial<Record<keyof ExecutiveFilterState, FilterOption[]>>

// Static option values for the enum-style filters. Numeric-ID filters
// (doctor/procedure/category/promotion) come from dynamicOptions at render
// time. No "—" sentinel row — multi-select Autocomplete uses an empty array
// to mean "no filter".
const STATIC_OPTIONS: OptionMap = {
  patientType: [
    { value: 'new', label: 'คนไข้ใหม่' },
    { value: 'existing', label: 'คนไข้เก่า' },
  ],
  // Mirror the APPT_STATUS enum used everywhere else in the system so this
  // filter passes real values the BE recognises. Labels get localised at
  // render time via APPOINTMENT.STATUS.* — same source the appointment list
  // and history screens use.
  appointmentStatus: [
    { value: 'NORMAL', label: 'ปกติ' },
    { value: 'CONFIRM', label: 'ตกลง' },
    { value: 'PUT_OFF', label: 'เลื่อนนัด' },
    { value: 'CAN_NOT_CONTACT', label: 'ติดต่อไม่ได้' },
    { value: 'NOT_YET', label: 'ยังไม่มา' },
    { value: 'ARRIVED', label: 'มาแล้ว' },
    { value: 'PENDING_SERVICE', label: 'รอรักษา' },
    { value: 'SERVICE', label: 'กำลังรักษา' },
    { value: 'PENDING_PAYMENT', label: 'รอชำระเงิน' },
    { value: 'PAYMENT', label: 'กำลังชำระเงิน' },
    { value: 'DONE', label: 'เสร็จสิ้น' },
    { value: 'CANCEL', label: 'ยกเลิก' },
    { value: 'CANCEL_APP', label: 'ยกเลิกผ่านแอปพลิเคชัน' },
  ],
  paymentType: [
    { value: 'cash', label: 'เงินสด' },
    { value: 'transfer', label: 'โอน' },
    { value: 'credit', label: 'บัตรเครดิต' },
    { value: 'debit', label: 'บัตรเดบิต' },
  ],
  nationality: [
    { value: 'TH', label: 'TH' },
    { value: 'JP', label: 'JP' },
    { value: 'CN', label: 'CN' },
    { value: 'MM', label: 'MM' },
    { value: 'EN', label: 'EN' },
    { value: 'OTHER', label: 'OTHER' },
  ],
  sourceChannel: [
    { value: 'facebook', label: 'Facebook' },
    { value: 'google', label: 'Google' },
    { value: 'line', label: 'LINE OA' },
    { value: 'referral', label: 'Referral' },
    { value: 'walkin', label: 'Walk-in' },
    { value: 'other', label: 'อื่นๆ' },
  ],
}

// Convert the slice value to the array shape Autocomplete consumes.
//   null     → []
//   number   → [n]
//   "1,3,5"  → [1, 3, 5]  (or strings if any token isn't numeric)
function toArrayValue(value: unknown): (string | number)[] {
  if (value == null || value === '') return []
  if (typeof value === 'number') return [value]
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => {
        const n = Number(s)
        return Number.isFinite(n) && /^-?\d+$/.test(s) ? n : s
      })
  }
  return []
}

// Reduce the Autocomplete array back to the slice shape.
//   []        → null
//   [n]       → n (preserve original type — number or string)
//   [a, b, …] → "a,b,…"
function fromArrayValue(picked: (string | number)[]): number | string | null {
  if (picked.length === 0) return null
  if (picked.length === 1) return picked[0]
  return picked.map(String).join(',')
}

const renderFilter = (
  key: keyof ExecutiveFilterState,
  value: unknown,
  onChange: (v: unknown) => void,
  t: (key: string, fallback?: string) => string,
  dynamicOptions: OptionMap
) => {
  // Pick whichever option source has data; fall back to the other. This
  // prevents the picker from collapsing to "—" while a dynamic source (e.g.
  // useDoctors) is still loading on first render — render the empty picker
  // instead so the field is always visible and refills once data arrives.
  const dyn = dynamicOptions[key]
  const stat = STATIC_OPTIONS[key]
  const options: FilterOption[] = dyn && dyn.length > 0
    ? dyn
    : stat && stat.length > 0
      ? stat
      : (dyn ?? stat ?? [])

  // ExcelCodeFilterSelect uses `value` as both display + unique-key, so we
  // pack `id = canonical filter value` (number id or enum code) and put the
  // human-readable label into `value` (= what the user sees + searches by).
  // Names CAN technically collide but it's a tolerable edge case in this
  // context. Nationality codes get localised before being used as labels.
  const rows: CodeRow[] = options.map((o) => {
    let label = o.label
    if (key === 'nationality' && typeof o.value === 'string') {
      label = t(`EXECUTIVE.FILTER.NATIONALITY_OPTIONS.${o.value}`, o.label)
    } else if (key === 'appointmentStatus' && typeof o.value === 'string') {
      label = t(`APPOINTMENT.STATUS.${o.value}`, o.label)
    }
    return { id: o.value, value: label, label }
  })

  const picked = toArrayValue(value)
  // Match by id (works for both numeric ids and enum-code strings) so the
  // selection survives a name change in the option list.
  const selectedRows = rows.filter((r) =>
    picked.some((p) => String(p) === String(r.id))
  )

  return (
    <ExcelCodeFilterSelect
      data={rows}
      selectedRows={selectedRows}
      // Parent already renders the field label + info tooltip above this
      // picker — pass a space so the component's own floating label
      // (top:-9 absolute, white bg) doesn't double up and collide with the
      // parent caption. A space keeps the Typography element mounted so
      // border-radius / colour transitions still work.
      inputLabel=" "
      placeholder={t('EXECUTIVE.FILTER.SEARCH_PLACEHOLDER', 'ค้นหา...')}
      maxTags={2}
      onSelectionChange={(picked) => {
        if (picked.length === 0) return onChange(null)
        const ids = picked.map((r) => r.id)
        // Preserve numeric vs string types — single pick keeps original type;
        // multi-pick joins as CSV string (backend accepts both via
        // buildIdInClause / patientTypeFromCsv).
        if (ids.length === 1) {
          const only = ids[0]
          return onChange(typeof only === 'number' ? only : String(only))
        }
        return onChange(ids.map(String).join(','))
      }}
    />
  )
}

// Maps filter key to i18n label key suffix. Explicit map avoids fragile transformation logic.
const LABEL_KEY: Record<keyof ExecutiveFilterState, string> = {
  dateRange: 'DATE_RANGE',
  branchId: 'BRANCH',
  doctorId: 'DOCTOR',
  procedureId: 'PROCEDURE',
  txCategoryId: 'TREATMENT_CATEGORY',
  patientType: 'PATIENT_TYPE',
  appointmentStatus: 'APPOINTMENT_STATUS',
  paymentType: 'PAYMENT_TYPE',
  nationality: 'NATIONALITY',
  sourceChannel: 'SOURCE_CHANNEL',
  promotionId: 'PROMOTION',
}

// Subset of ExecutiveFilterState the drawer can edit (excludes dateRange,
// branchId — those live on the inline filter bar / are
// drill-only props).
type DrawerFilterKey = Exclude<keyof ExecutiveFilterState, 'dateRange' | 'branchId'>
type DraftValues = Partial<Pick<ExecutiveFilterState, DrawerFilterKey>>

export default function FilterDrawer({ tab, open, onClose }: Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const appliedFilters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)
  const { data: promotions } = usePromotions()
  const { data: doctors, loading: doctorsLoading } = useDoctors(appliedFilters.branchId)
  const { data: procedures } = useProcedures()
  const { data: opTypes } = useOperativeTypes()

  const keys = EXECUTIVE_TAB_FILTER_KEYS[tab]

  // When the branch changes, a doctor who is not attached to the new branch
  // would silently zero every widget — drop them from the selection instead
  // (MD-2609-1162). Skipped while the branch list is still loading.
  useEffect(() => {
    if (doctorsLoading || appliedFilters.doctorId == null) return
    const pruned = pruneIdSelection(appliedFilters.doctorId, doctors.map((d) => d.id))
    if (pruned !== appliedFilters.doctorId) dispatch(setFilter({ key: 'doctorId', value: pruned }))
  }, [doctorsLoading, doctors, appliedFilters.doctorId, dispatch])

  // Draft mode — edits stay local until the user hits Apply.
  const [draft, setDraft] = useState<DraftValues>(() => collectDraft(appliedFilters, keys))
  useEffect(() => {
    if (open) setDraft(collectDraft(appliedFilters, keys))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab])

  const draftDirty = useMemo(() => {
    return keys.some((k) => {
      const drawerKey = k as DrawerFilterKey
      return draft[drawerKey] !== appliedFilters[drawerKey]
    })
  }, [draft, appliedFilters, keys])

  const dynamicOptions: OptionMap = useMemo(() => ({
    promotionId: promotions.map((p) => ({ value: p.id, label: p.name })),
    doctorId: doctors.map((d) => ({ value: d.id, label: d.name })),
    procedureId: procedures.slice(0, 200).map((p) => ({ value: p.id, label: p.name })),
    txCategoryId: opTypes.map((o) => ({ value: o.id, label: o.name })),
  }), [promotions, doctors, procedures, opTypes])

  const handleApply = () => {
    keys.forEach((k) => {
      const drawerKey = k as DrawerFilterKey
      if (draft[drawerKey] !== appliedFilters[drawerKey]) {
        dispatch(setFilter({ key: drawerKey, value: draft[drawerKey] as never }))
      }
    })
    onClose()
  }

  const handleResetDraft = () => {
    const cleared: DraftValues = {}
    keys.forEach((k) => {
      cleared[k as DrawerFilterKey] = null as never
    })
    setDraft(cleared)
  }

  const handleClose = () => {
    setDraft(collectDraft(appliedFilters, keys))
    onClose()
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ width: 380, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="h6">{t('EXECUTIVE.FILTER.ADVANCED_BUTTON')}</Typography>
          <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t(`EXECUTIVE.FILTER.TAB_DESCRIPTION.${tab}`)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 2, color: colors.themeSecondColor, fontStyle: 'italic' }}
        >
          {t(
            'EXECUTIVE.FILTER.DRAFT_HINT',
            'การเปลี่ยนแปลงจะมีผลเมื่อกด "ใช้ตัวกรอง"'
          )}
        </Typography>

        <Stack spacing={2}>
          {keys.map((k) => (
            <Box key={k}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {t(`EXECUTIVE.FILTER.${LABEL_KEY[k]}`)}
                </Typography>
                <Tooltip
                  title={t(`EXECUTIVE.FILTER.DESC.${LABEL_KEY[k]}`)}
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360 } },
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }}
                  />
                </Tooltip>
              </Box>
              {renderFilter(
                k,
                draft[k as DrawerFilterKey],
                (v) => setDraft((prev) => ({ ...prev, [k]: v })),
                t,
                dynamicOptions,
              )}
            </Box>
          ))}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 3 }}>
          <Button
            // Clears every active advanced filter — including ones this
            // tab's drawer does not show (e.g. a doctor set on sales while
            // viewing account) — then closes so the chips/badge visibly
            // disappear; nothing is left pending behind a disabled Apply
            // (MD-2609-1161 TC-06).
            onClick={() => {
              dispatch(resetFilters())
              handleResetDraft()
              onClose()
            }}
            color="inherit"
            size="small"
          >
            {t('EXECUTIVE.FILTER.RESET')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleClose}
              size="small"
              sx={{ color: colors.gray, '&:hover': { color: colors.themeMainColor } }}
            >
              {t('EXECUTIVE.FILTER.CANCEL', 'ยกเลิก')}
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!draftDirty}
              size="small"
              sx={{
                backgroundColor: colors.themeSecondColor,
                '&:hover': { backgroundColor: colors.themeSecondDarkColor },
                '&.Mui-disabled': { backgroundColor: colors.lightGray },
              }}
            >
              {t('EXECUTIVE.FILTER.APPLY')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

function collectDraft(
  filters: ExecutiveFilterState,
  keys: (keyof ExecutiveFilterState)[]
): DraftValues {
  const draft: DraftValues = {}
  keys.forEach((k) => {
    const drawerKey = k as DrawerFilterKey
    draft[drawerKey] = filters[drawerKey] as never
  })
  return draft
}
